import { normalizeId } from './ids';
import { isOwnedByCollection } from './cardScopes';
import { descendantCardIdsOf, inheritedCardIdsOf } from './collectionFamily';

/**
 * Helpers for reading collection records. The API has historically returned
 * sharedWith as either an array or a comma-separated string, so all reads go
 * through toStringList.
 */
export const toStringList = value => {
	if (Array.isArray(value)) return value;
	if (typeof value === 'string') {
		return value.split(',').map(entry => entry.trim()).filter(Boolean);
	}
	return [];
};

export const getSharedWith = collection => toStringList(collection?.sharedWith);

/**
 * A collection's card membership: collection.cards[] holds entries shaped
 * { cardDefinitionId, name, favorite }.
 *
 * `favorite` is the *collection* favorite — a manager pins a card in collection
 * settings and it stays pinned for every user of the collection. That is
 * deliberately different from the per-user favorites in FavoritesContext, which
 * each user sets for themselves; a card can be both.
 */
export const getCollectionCards = collection => (
	Array.isArray(collection?.cards) ? collection.cards : []
);

export const getCollectionCardIds = collection => new Set(
	getCollectionCards(collection)
		.map(ref => normalizeId(ref?.cardDefinitionId))
		.filter(Boolean)
);

export const getCollectionFavoriteCardIds = collection => new Set(
	getCollectionCards(collection)
		.filter(ref => !!ref?.favorite)
		.map(ref => normalizeId(ref?.cardDefinitionId))
		.filter(Boolean)
);

/**
 * The cards a collection OWNS or has attached — mirrors the API's two-source
 * model: the embedded collection.cards[] refs PLUS every card definition
 * OWNED by the collection (scope 'collection'), which is a member whether or
 * not a config entry exists for it yet. Pass the card-definition list
 * (usually from CardDefinitionsContext) for the ownership half. What the Card
 * Library manages; not what the collection can USE (below).
 */
export const getCollectionOwnCardIds = (collection, cardDefs = []) => {
	const ids = getCollectionCardIds(collection);
	const collectionId = normalizeId(collection?._id);
	if (collectionId) {
		(cardDefs || []).forEach(cd => {
			if (isOwnedByCollection(cd, collectionId)) {
				const id = normalizeId(cd?._id);
				if (id) ids.add(id);
			}
		});
	}
	return ids;
};

/**
 * FULL card membership for a collection: the cards usable in it — its own
 * (above) plus, for a sub-collection, every card it inherits from its parent
 * (`hierarchy.inheritedCardIds`, utils/collectionFamily.js; keepr-api §
 * Sub-collections "effective cards"). An inherited card is simply one of the
 * sub-collection's cards to everything that reads this: the picker, the
 * list's vocabulary, favorites, quick adds, grants.
 */
export const getCollectionMemberCardIds = (collection, cardDefs = []) => {
	const ids = getCollectionOwnCardIds(collection, cardDefs);
	inheritedCardIdsOf(collection).forEach(id => ids.add(id));
	return ids;
};

/**
 * The cards a collection's LIST can hold: its member cards, plus — at a
 * parent collection — every sub-collection's own cards (`familyCardIds`).
 * The same as the member set for a collection with no sub-collections.
 */
export const getCollectionFamilyCardIds = (collection, cardDefs = []) => {
	const ids = getCollectionMemberCardIds(collection, cardDefs);
	descendantCardIdsOf(collection).forEach(id => ids.add(id));
	return ids;
};

/**
 * A collection with no cards at all: none of its own, none inherited, and —
 * at a parent — none in any sub-collection either. The family is the test,
 * not the member set: a parent whose cards all live in its sub-collections
 * has a list that can hold items, so telling its people to "start with a
 * card" would be wrong (KPR-322).
 */
export const collectionHasNoCards = (collection, cardDefs = []) => (
	Boolean(collection) && getCollectionFamilyCardIds(collection, cardDefs).size === 0
);

/**
 * The card keys a collection NAMES as facets (`collection.facets`, an array of
 * `{ cardKey }` — docs/plans/card-editor-dates-groups-filters.md Part 5's wire
 * contract). Absent or empty means AUTOMATIC: every target card that two or
 * more differently named lookups point at earns a facet on its own
 * (utils/facets.js). Naming a card promotes it on a single element.
 */
export const getCollectionFacetCardKeys = collection => new Set(
	(Array.isArray(collection?.facets) ? collection.facets : [])
		.map(entry => (typeof entry === 'string' ? entry : entry?.cardKey))
		.map(key => (typeof key === 'string' ? key.trim() : ''))
		.filter(Boolean)
);

/**
 * The stored shape for a list of card keys: `[{ cardKey }]`.
 *
 * An empty list stays an EMPTY ARRAY rather than becoming `undefined`. The
 * collection PUT is a `$set` of the payload, so a key that is not in the JSON
 * is a key Mongo leaves exactly as it was: emitting `undefined` would mean
 * clearing the setting saved nothing and the old override came back on the
 * next load. `[]` and absent read the same way coming in
 * (`getCollectionFacetCardKeys` — both mean AUTOMATIC), so writing the empty
 * array costs nothing and is the only spelling that can erase a stored list.
 */
export const toCollectionFacets = cardKeys => {
	const keys = [...new Set((cardKeys || []).map(key => (typeof key === 'string' ? key.trim() : '')).filter(Boolean))];
	return keys.map(cardKey => ({ cardKey }));
};

/**
 * `collection.quickFilters` — which of the chips a collection HAS the toolbar
 * pins or hides. A sibling of `facets`, never an overload of it: `facets`
 * answers which cards EARN a chip, this answers which of the chips we have the
 * toolbar shows.
 *
 * Each entry is a CHIP KEY in the spelling the field descriptor already wears
 * (`pathKey`): `@<card-key>` for a facet, the bare element name for an element
 * an editor promoted with `options.quickFilter`.
 *
 * PRESENTATION ONLY. A hidden chip's condition still parses, still compiles,
 * still filters and still shows in the applied-chips row — hiding a chip never
 * changes what a stored query means.
 */
const quickFilterList = (collection, list) => (
	(Array.isArray(collection?.quickFilters?.[list]) ? collection.quickFilters[list] : [])
		.map(key => (typeof key === 'string' ? key.trim() : ''))
		.filter(Boolean)
);

/** The pinned chip keys, IN AUTHOR ORDER — the order is the setting. */
export const getCollectionQuickFilterPins = collection => quickFilterList(collection, 'pinned');

/** The chip keys the toolbar never offers. Order is meaningless, so a Set. */
export const getCollectionHiddenQuickFilters = collection => new Set(quickFilterList(collection, 'hidden'));

/**
 * The stored shape: `{ pinned: [...], hidden: [...] }`, both always present.
 *
 * An empty list stays an EMPTY ARRAY rather than becoming `undefined`, for
 * exactly the reason `toCollectionFacets` documents: the collection PUT is a
 * `$set` of the payload, so a key that is not in the JSON is a key Mongo
 * leaves alone. Emitting `undefined` would mean "unhide everything" silently
 * saved nothing and the old list came back on the next load. `[]` and absent
 * read the same way coming in, so writing the empty array costs nothing and is
 * the only spelling that can erase a stored list.
 */
export const toCollectionQuickFilters = (pinned = [], hidden = []) => {
	const clean = keys => [...new Set(
		(keys || []).map(key => (typeof key === 'string' ? key.trim() : '')).filter(Boolean)
	)];
	return { pinned: clean(pinned), hidden: clean(hidden) };
};

/**
 * `list` in the person's favorites order — FavoritesContext
 * `favoriteCollectionOrder`, collection ids in userFavorites `position` —
 * so Home's Collections strip, the collections page's Favorites and the
 * rail's Pinned read ONE order. Entries the order does not name keep their
 * relative order after the ones it does; an empty order (not loaded yet)
 * leaves the list as it came. `getId` reads an entry's collection id (a
 * collection by default; a Home pin row's is its `target_id`). Pure.
 */
export const orderByFavorites = (list, order, getId = entry => entry?._id) => {
	const rows = Array.isArray(list) ? list : [];
	if (!Array.isArray(order) || !order.length) return rows;
	const rank = new Map(order.map((id, index) => [normalizeId(id), index]));
	const last = order.length;
	return rows
		.map((entry, index) => ({ entry, index, at: rank.has(normalizeId(getId(entry))) ? rank.get(normalizeId(getId(entry))) : last }))
		.sort((a, b) => (a.at - b.at) || (a.index - b.index))
		.map(row => row.entry);
};
